import {
  ExclamationCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { Card, Spin, Col, Row, Statistic, Table, Tag, Button } from "antd";
import Title from "antd/es/typography/Title";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useTheme } from "@src/providers/theme/useTheme";
import { formatAssetErrorId } from "@src/utils/assetErrorHelpers";

import AssetErrorActions from "./AssetErrorActions";
import AssetErrorVideos from "./AssetErrorVideos";
import { getFragmentData } from "../../graphql";
import {
  AssetErrorDocument,
  AssetErrorVideoFragmentDoc,
  AssetErrorSurroundingAssetSignalFragmentDoc,
} from "../../graphql/graphql";
import { formatDate } from "../../utils/dateHelpers";

const AssetError = () => {
  const { factoryId, assetId, assetSignalId } = useParams<{
    factoryId: string;
    assetId: string;
    assetSignalId: string;
  }>();

  const { theme } = useTheme();

  const { data, loading, error } = useQuery(AssetErrorDocument, {
    fetchPolicy: "network-only",
    skip: !factoryId || !assetId || !assetSignalId,
    variables: {
      factoryId: factoryId ?? "",
      assetId: assetId ?? "",
      assetSignalId: assetSignalId ?? "",
    },
  });

  const [playAllButtonClicked, setPlayAllButtonClicked] = useState(false);

  const handlePlayAllButtonClick = () => {
    setPlayAllButtonClicked(true);
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin />
      </div>
    );

  if (error) return <h2>GraphQL Error</h2>;

  if (data === undefined) return <h2>ERROR</h2>;

  const assetSignal =
    data.factory.__typename === "QueryFactorySuccess" &&
    data.factory.data.asset.__typename === "FactoryAssetSuccess" &&
    data.factory.data.asset.data.assetSignal.__typename ===
      "AssetAssetSignalSuccess"
      ? data.factory.data.asset.data.assetSignal.data
      : null;
  const assetError = assetSignal?.assetError;
  const videoFragments = assetError?.videos.map((video) =>
    getFragmentData(AssetErrorVideoFragmentDoc, video),
  );
  const surroundingAssetSignals = assetError?.surroundingAssetSignals.map(
    (signal) =>
      getFragmentData(AssetErrorSurroundingAssetSignalFragmentDoc, signal),
  );

  if (!assetError || !videoFragments || !surroundingAssetSignals)
    return <h2>Not found</h2>;

  const isCurrentAssetError = (assetErrorId: string) =>
    assetErrorId === assetSignal.assetError?.id;

  const columns = [
    {
      title: "Signal",
      dataIndex: "signal",
      key: "signal",
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: string) => formatDate(timestamp),
    },
    {
      title: "Signal ID",
      dataIndex: "id",
      key: "id",
      render: (id: string) => formatAssetErrorId(id),
    },
    {
      title: "Asset Error",
      dataIndex: "assetError",
      key: "assetError",
      render: (assetError: { id: string } | null) => {
        if (!assetError) {
          return "-";
        }
        return (
          <Tag color="red" icon={<ExclamationCircleOutlined />}>
            {isCurrentAssetError(assetError.id) ? "Current Error" : "Error"}
          </Tag>
        );
      },
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginTop: 20 }}>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic title="Signal" value={assetSignal.signal} />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Timestamp"
              value={formatDate(assetSignal.timestamp)}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Handled At"
              value={
                assetError.handledAt
                  ? formatDate(assetError.handledAt)
                  : "Not handled"
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="Resolved At"
              value={
                assetError.resolvedAt
                  ? formatDate(assetError.resolvedAt)
                  : "Not resolved"
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        bordered={false}
        style={{ marginTop: "20px" }}
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Error Videos
            <div
              style={{
                marginLeft: "auto",
                marginRight: "10px",
              }}
            >
              <Button type="primary" onClick={handlePlayAllButtonClick}>
                <PlayCircleOutlined /> Play all
              </Button>
            </div>
          </div>
        }
        extra={<AssetErrorActions assetError={assetError} />}
      >
        {videoFragments.length === 0 ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Spin />
          </div>
        ) : (
          <AssetErrorVideos
            videos={videoFragments}
            playAllButtonClicked={playAllButtonClicked}
          />
        )}
      </Card>

      <Card
        bordered={false}
        style={{ marginTop: "20px" }}
        title="Surrounding Asset Signals"
      >
        {/* This should not happen because the asset error will always be part of this array */}
        {surroundingAssetSignals.length === 0 ? (
          <Title level={4}>No surrounding asset signals available</Title>
        ) : (
          <Table
            dataSource={surroundingAssetSignals}
            columns={columns}
            rowClassName={(record) =>
              record.assetError && isCurrentAssetError(record.assetError.id)
                ? "highlighted-row"
                : ""
            }
          />
        )}
      </Card>

      <style>{`
  .highlighted-row {
    background-color: ${theme === "dark" ? "#3b1f1f" : "#fff1f0"};
  }
`}</style>
    </>
  );
};

export default AssetError;
